import { ChangeEvent, useState, useEffect, useMemo } from 'react';
import { Button, CheckBox, Label } from '@iwoca/orion';
import { t, Trans } from '@lingui/macro';
import clsx from 'clsx';
import { TFilterItem, TToggledValue } from 'types';
import { arraysHaveSameItems } from 'utils/common/common';
import { flattenItemValues, getGroupedItems } from './ListFilter.helpers';
import styles from './ListFilter.module.css';

type TProps = {
  items: TFilterItem[];
  defaultValues?: string[];
  values?: string[];
  allSelectedByDefault?: boolean;
  className?: string;
  onChange?: (
    selectedValues: string[],
    lastToggledValue: TToggledValue | null,
  ) => void;
  onSave?: (selectedValues: string[]) => void;
  onClear?: () => void;
  onCancel?: () => void;
  title?: string;
  subTitle?: string;
  variant?: 'container' | 'normal';
  disableSaveUntilChanged?: boolean;
  isLoading?: boolean;
};

export const ListFilter = ({
  items,
  defaultValues,
  values,
  allSelectedByDefault = true,
  className,
  onChange,
  onSave,
  onCancel,
  onClear,
  title,
  subTitle,
  variant = 'container',
  disableSaveUntilChanged = false,
  isLoading = false,
}: TProps) => {
  const allValues = flattenItemValues(items);
  const initialValues =
    values || defaultValues || (allSelectedByDefault ? allValues : []);

  const [selectedValues, setSelectedValues] = useState(initialValues);
  const [previousSelectedValues, setPreviousSelectedValues] =
    useState(initialValues);

  const [lastToggledValue, setLastToggledValue] =
    useState<TToggledValue | null>(null);

  const handleChange = ({ target: { name } }: ChangeEvent<any>) => {
    setLastToggledValue({ item: name, value: !selectedValues.includes(name) });
    setSelectedValues((prevValues) => {
      if (prevValues.includes(name)) {
        return prevValues.filter((val) => val !== name);
      } else {
        return [...prevValues, name];
      }
    });
  };

  const handleChangeSelectAll = () => {
    if (allValues.length !== selectedValues.length) {
      setSelectedValues(allValues);
    }
  };

  const handleClearFilters = () => {
    setSelectedValues([]);
    if (!onClear) {
      return null;
    }
    onClear();
  };

  useEffect(() => {
    if (!onChange) {
      return;
    }
    onChange(selectedValues, lastToggledValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedValues]);

  useEffect(() => {
    setSelectedValues(initialValues);
    setPreviousSelectedValues(initialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(items), values, allSelectedByDefault, defaultValues]);

  const hasChanged = useMemo(
    () => !arraysHaveSameItems(selectedValues, previousSelectedValues),
    [selectedValues, previousSelectedValues],
  );

  return (
    <div
      className={clsx(
        styles.listFilter,
        styles[`variant-${variant}`],
        className,
      )}
    >
      {title && <h4 className={styles.title}>{title}</h4>}
      {subTitle && <p className={styles.subTitle}>{subTitle}</p>}

      {variant === 'container' && (
        <div className={styles.generalFilters}>
          <label className={clsx(styles.filterItem, styles.selectAllFilter)}>
            <CheckBox
              className={styles.filterCheckbox}
              checked={selectedValues.length === allValues.length}
              onChange={handleChangeSelectAll}
              data-testid="select-all-checkbox"
              disabled={selectedValues.length === allValues.length}
            />
            <span className={styles.filterLabel}>{t`Select all`}</span>
          </label>
          <button className={styles.clearButton} onClick={handleClearFilters}>
            <Trans>Clear</Trans>
          </button>
        </div>
      )}

      <div className={styles['items']}>
        {getGroupedItems(items).map(({ group, items }) => (
          <div
            key={group}
            className={clsx(styles.group, {
              ungrouped: group === 'ungrouped',
            })}
          >
            {group !== 'ungrouped' && (
              <h4 className={styles.groupTitle}>{group}</h4>
            )}
            {items.map(({ value, label }) => (
              <div className={styles.filterItem} key={value}>
                <CheckBox
                  id={group + value}
                  name={value}
                  className={styles.filterCheckbox}
                  checked={selectedValues.includes(value)}
                  onChange={handleChange}
                  data-testid={`${value}-checkbox`}
                />
                <Label htmlFor={group + value} className={styles.filterLabel}>
                  {label || value}
                </Label>
              </div>
            ))}
          </div>
        ))}
      </div>

      <div className={styles.options}>
        {onCancel && (
          <Button variant="tertiary" onClick={() => onCancel()}>
            <Trans>Cancel</Trans>
          </Button>
        )}
        <Button
          loading={isLoading}
          variant="primary"
          disabled={disableSaveUntilChanged && !hasChanged}
          onClick={() => onSave?.(selectedValues)}
        >
          <Trans>Apply</Trans>
        </Button>
      </div>
    </div>
  );
};
